import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export interface BusinessProfile {
	master_merchant_id: number;
	business_name: string;
	state_name: string;
	state_code: string;
	farmart_id: string;
	/** OS merchant_stages / supplierKycEnumStatus (e.g. DRAFT=15). */
	kyc_status_id?: number;
}

/**
 * `idle` / `loading` — list not trusted yet (cold start list is always []).
 * `ready` — last `business_profiles` fetch applied; ownership is authoritative.
 * `error` — fetch failed; do not treat empty list as "no businesses".
 */
export type BusinessProfilesStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Mirror of supplierKycEnumStatus — kept local so this package stays
 * dependency-light. Must stay in sync with common-utils kycEnums.
 */
export const KYC_STATUS = {
	DRAFT: 15,
	DRAFT_INACTIVE: 16,
} as const;

/** In-progress Self KYC merchant — resume/wizard only, never Trade APIs. */
export function isDraftKycStatus(
	kycStatusId: number | null | undefined,
): boolean {
	return kycStatusId === KYC_STATUS.DRAFT;
}

/** Inactive draft — not selectable for Self KYC or Trade. */
export function isDraftInactiveKycStatus(
	kycStatusId: number | null | undefined,
): boolean {
	return kycStatusId === KYC_STATUS.DRAFT_INACTIVE;
}

/**
 * Merchants that may be used for Trade / ledger / membership / non-KYC APIs.
 * DRAFT is excluded: incomplete Self KYC must not drive create_sauda etc.
 */
export function isTradingEligibleKycStatus(
	kycStatusId: number | null | undefined,
): boolean {
	if (kycStatusId == null) return true;
	return (
		!isDraftKycStatus(kycStatusId) && !isDraftInactiveKycStatus(kycStatusId)
	);
}

interface BusinessProfileState {
	selectedBusinessProfile: BusinessProfile | null;
	businessProfileList: BusinessProfile[];
	profilesStatus: BusinessProfilesStatus;

	setSelectedBusinessProfile: (profile: BusinessProfile | null) => void;
	setBusinessProfileList: (list: BusinessProfile[]) => void;
	updateSelectedBusinessProfile: (
		updatedProfile: Partial<BusinessProfile>,
	) => void;
	/** After Self KYC TDS submit — sync terminal kyc_status_id on selected + list. */
	patchBusinessProfileKycStatus: (
		masterMerchantId: number,
		kycStatusId: number,
	) => void;
	clearSelectedBusinessProfile: () => void;
	clearBusinessProfileList: () => void;

	markBusinessProfilesLoading: () => void;
	markBusinessProfilesError: () => void;

	/**
	 * Apply login-scoped `business_profiles` result.
	 * Drops persisted selection when that merchant is not on this login.
	 * Default pick prefers a trading-eligible row over DRAFT.
	 */
	reconcileFromBusinessProfiles: (list: BusinessProfile[]) => void;

	/**
	 * Self KYC create: select merchant and upsert into the in-memory list
	 * so reconcile does not wipe a just-created DRAFT before list refresh.
	 */
	adoptBusinessProfile: (profile: BusinessProfile) => void;

	/**
	 * @deprecated Prefer selectSelfKycMasterMerchantId / selectTradingMasterMerchantId.
	 * Owned selected id (any status including DRAFT) when profiles are ready.
	 */
	getOwnedMasterMerchantId: () => number | null | undefined;
}

function isOwned(
	list: BusinessProfile[],
	masterMerchantId: number | null | undefined,
): boolean {
	if (masterMerchantId == null) return false;
	return list.some(profile => profile.master_merchant_id === masterMerchantId);
}

function preferDefaultProfile(list: BusinessProfile[]): BusinessProfile | null {
	if (list.length === 0) return null;
	const trading = list.find(p => isTradingEligibleKycStatus(p.kyc_status_id));
	return trading ?? list[0] ?? null;
}

function resolveOwnedSelected(
	state: Pick<
		BusinessProfileState,
		'profilesStatus' | 'selectedBusinessProfile' | 'businessProfileList'
	>,
): BusinessProfile | null {
	if (state.profilesStatus !== 'ready') return null;
	const selected = state.selectedBusinessProfile;
	if (!selected) return null;
	if (!isOwned(state.businessProfileList, selected.master_merchant_id)) {
		return null;
	}
	return selected;
}

export const useBusinessProfileStore = create<BusinessProfileState>()(
	devtools(
		persist(
			(set, get) => ({
				selectedBusinessProfile: null,
				businessProfileList: [],
				profilesStatus: 'idle',

				setSelectedBusinessProfile: profile =>
					set({ selectedBusinessProfile: profile }),

				setBusinessProfileList: list => set({ businessProfileList: list }),

				updateSelectedBusinessProfile: updatedProfile =>
					set(state => ({
						selectedBusinessProfile: state.selectedBusinessProfile
							? { ...state.selectedBusinessProfile, ...updatedProfile }
							: null,
					})),

				patchBusinessProfileKycStatus: (masterMerchantId, kycStatusId) =>
					set(state => {
						const patchProfile = (profile: BusinessProfile): BusinessProfile =>
							profile.master_merchant_id === masterMerchantId
								? { ...profile, kyc_status_id: kycStatusId }
								: profile;
						return {
							businessProfileList: state.businessProfileList.map(patchProfile),
							selectedBusinessProfile:
								state.selectedBusinessProfile?.master_merchant_id === masterMerchantId
									? patchProfile(state.selectedBusinessProfile)
									: state.selectedBusinessProfile,
						};
					}),

				clearSelectedBusinessProfile: () => set({ selectedBusinessProfile: null }),

				clearBusinessProfileList: () =>
					set({ businessProfileList: [], profilesStatus: 'idle' }),

				markBusinessProfilesLoading: () => set({ profilesStatus: 'loading' }),

				markBusinessProfilesError: () => set({ profilesStatus: 'error' }),

				reconcileFromBusinessProfiles: list => {
					const { selectedBusinessProfile: selected, businessProfileList } = get();
					const selectedId = selected?.master_merchant_id ?? null;

					// A Self KYC DRAFT adopted in this session may not be in a
					// business_profiles response that was already in flight. Keep it
					// rather than dropping the merchant the wizard is bound to.
					// Cold start cannot hit this: the in-memory list is [] until a
					// fetch or adopt, so a stale persisted selection is still dropped.
					const keepAdoptedDraft =
						selected != null &&
						isDraftKycStatus(selected.kyc_status_id) &&
						isOwned(businessProfileList, selectedId) &&
						!isOwned(list, selectedId);

					const nextList = keepAdoptedDraft
						? [selected as BusinessProfile, ...list]
						: list;

					let nextSelected: BusinessProfile | null;
					if (selectedId != null) {
						const match = nextList.find(p => p.master_merchant_id === selectedId);
						nextSelected = match ?? preferDefaultProfile(nextList);
					} else {
						nextSelected = preferDefaultProfile(nextList);
					}
					set({
						businessProfileList: nextList,
						selectedBusinessProfile: nextSelected,
						profilesStatus: 'ready',
					});
				},

				adoptBusinessProfile: profile => {
					const list = get().businessProfileList;
					const without = list.filter(
						p => p.master_merchant_id !== profile.master_merchant_id,
					);
					const nextProfile: BusinessProfile = {
						...profile,
						// Self KYC create path only — always DRAFT until TDS submit.
						// Ignore caller status so a prior approved selection cannot
						// leak into this merchant and unlock Trade selectors.
						kyc_status_id: KYC_STATUS.DRAFT,
					};
					set({
						selectedBusinessProfile: nextProfile,
						businessProfileList: [nextProfile, ...without],
						// Always ready after adopt so Self KYC can bind the new id
						// even if a concurrent list fetch is still in flight.
						profilesStatus: 'ready',
					});
				},

				getOwnedMasterMerchantId: () => {
					const owned = resolveOwnedSelected(get());
					if (get().profilesStatus !== 'ready') return undefined;
					return owned?.master_merchant_id ?? null;
				},
			}),
			{
				name: 'business-profile-store',
				partialize: state => ({
					selectedBusinessProfile: state.selectedBusinessProfile,
				}),
				merge: (persisted, current) => {
					const partial = (persisted ?? {}) as Partial<BusinessProfileState>;
					return {
						...current,
						...partial,
						businessProfileList: current.businessProfileList,
						profilesStatus: 'idle',
					};
				},
			},
		),
		{ name: 'business-profile-store' },
	),
);

/**
 * Owned selected id (any status except DRAFT_INACTIVE).
 * Use for My Profile KYC consent / selected-business KYC UI — not Trade,
 * and not Self KYC draft-sibling resume.
 */
export function selectOwnedSelectedMasterMerchantId(
	state: BusinessProfileState,
): number | null | undefined {
	if (state.profilesStatus !== 'ready') return undefined;
	const selected = resolveOwnedSelected(state);
	if (!selected) return null;
	if (isDraftInactiveKycStatus(selected.kyc_status_id)) return null;
	return selected.master_merchant_id;
}

/**
 * Master merchant for Self KYC wizard / draft resume / bid-skip nudge.
 * Prefers a DRAFT row in the list (Continue KYC) even when the switcher
 * selection is a different trading-eligible business.
 * When several DRAFTs exist, prefers the one matching selection, else newest id.
 * `undefined` = profiles not ready; `null` = ready but no Self-KYC id.
 */
export function selectSelfKycMasterMerchantId(
	state: BusinessProfileState,
): number | null | undefined {
	if (state.profilesStatus !== 'ready') return undefined;

	const drafts = state.businessProfileList.filter(p =>
		isDraftKycStatus(p.kyc_status_id),
	);
	if (drafts.length > 0) {
		const selectedId = state.selectedBusinessProfile?.master_merchant_id;
		const selectedDraft = drafts.find(p => p.master_merchant_id === selectedId);
		if (selectedDraft) return selectedDraft.master_merchant_id;
		return Math.max(...drafts.map(p => p.master_merchant_id));
	}

	return selectOwnedSelectedMasterMerchantId(state);
}

/**
 * Owned selected id excluding DRAFT — for Trade, sauda, ledger, membership,
 * GST, homescreen insights, etc.
 */
export function selectTradingMasterMerchantId(
	state: BusinessProfileState,
): number | null | undefined {
	if (state.profilesStatus !== 'ready') return undefined;
	const selected = resolveOwnedSelected(state);
	if (!selected) return null;
	if (!isTradingEligibleKycStatus(selected.kyc_status_id)) return null;
	return selected.master_merchant_id;
}

/** @deprecated Prefer selectSelfKyc / selectTrading / selectOwnedSelected. */
export function selectOwnedMasterMerchantId(
	state: BusinessProfileState,
): number | null | undefined {
	return selectOwnedSelectedMasterMerchantId(state);
}
